import type {
  MetaFieldType,
  MetaSchemaField,
} from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import type { ProjectConfig } from "./types";

const META_FIELD_TYPES: MetaFieldType[] = ["string", "string[]", "int", "bool"];

/**
 * Coerce `profile.meta_schema` into well-formed entries. Unlike the other
 * schema arrays this one is nested (`allowed` is itself a list), and the blob
 * is free-form JSONB the backend never rewrites — so a hand-edited config can
 * carry junk that would otherwise crash the editor. Entries without a usable
 * type are dropped; a malformed `allowed` degrades to "no allow-list".
 */
export function normalizeMetaSchema(input: unknown): MetaSchemaField[] {
  if (!Array.isArray(input)) return [];
  const out: MetaSchemaField[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Partial<MetaSchemaField>;
    if (typeof entry.key !== "string") continue;
    if (!META_FIELD_TYPES.includes(entry.type as MetaFieldType)) continue;
    out.push({
      key: entry.key,
      type: entry.type as MetaFieldType,
      allowed: Array.isArray(entry.allowed)
        ? entry.allowed.filter((v): v is string => typeof v === "string")
        : [],
    });
  }
  return out;
}

/**
 * Server-side defaults that the UI falls back to when a field is unset on the
 * loaded config. Mirrors the values quoted in the mockup
 * (project-config.html L463 "500", L479 "200", L494 "20", L535 "30", L617
 * "3600", L631 "1", L653 "30", L664 "5"). Numbers err on the side of caution
 * matching the platform's documented defaults.
 */
export const DEFAULT_CONFIG: ProjectConfig = {
  chat: {
    history_window: 500,
    max_message_length: 200,
    rate_limit_per_minute: 20,
    profanity_words: [],
  },
  guild: {
    max_members: 30,
    require_unique_name: false,
    metadata_schema: [],
  },
  help_request: {
    types: [],
    default_ttl_seconds: 3600,
    max_active_per_player: 3,
  },
  rate_limits: {
    auth_per_ip_per_minute: 60,
    help_create_per_minute: 10,
  },
  profile: {
    stats_schema: [],
    meta_schema: [],
    allowed_avatars: [],
  },
  guild_assets: {
    allowed_logos: [],
  },
};

/**
 * Coerce an unknown blob into a ProjectConfig, filling missing keys from the
 * default. Used on initial load so the form always has a complete shape to
 * bind against even if the backend returns partial config.
 */
export function normalizeConfig(input: unknown): ProjectConfig {
  const root = (input ?? {}) as Partial<Record<keyof ProjectConfig, unknown>>;
  const chat = (root.chat ?? {}) as Partial<ProjectConfig["chat"]>;
  const guild = (root.guild ?? {}) as Partial<ProjectConfig["guild"]>;
  const help = (root.help_request ?? {}) as Partial<
    ProjectConfig["help_request"]
  >;
  const rate = (root.rate_limits ?? {}) as Partial<
    ProjectConfig["rate_limits"]
  >;
  const profile = (root.profile ?? {}) as Partial<ProjectConfig["profile"]>;
  const guildAssets = (root.guild_assets ?? {}) as Partial<
    ProjectConfig["guild_assets"]
  >;

  return {
    chat: {
      history_window:
        typeof chat.history_window === "number"
          ? chat.history_window
          : DEFAULT_CONFIG.chat.history_window,
      max_message_length:
        typeof chat.max_message_length === "number"
          ? chat.max_message_length
          : DEFAULT_CONFIG.chat.max_message_length,
      rate_limit_per_minute:
        typeof chat.rate_limit_per_minute === "number"
          ? chat.rate_limit_per_minute
          : DEFAULT_CONFIG.chat.rate_limit_per_minute,
      profanity_words: Array.isArray(chat.profanity_words)
        ? chat.profanity_words.filter((w): w is string => typeof w === "string")
        : [],
    },
    guild: {
      max_members:
        typeof guild.max_members === "number"
          ? guild.max_members
          : DEFAULT_CONFIG.guild.max_members,
      require_unique_name:
        typeof guild.require_unique_name === "boolean"
          ? guild.require_unique_name
          : DEFAULT_CONFIG.guild.require_unique_name,
      metadata_schema: Array.isArray(guild.metadata_schema)
        ? (guild.metadata_schema as ProjectConfig["guild"]["metadata_schema"])
        : [],
    },
    help_request: {
      types: Array.isArray(help.types)
        ? help.types.filter((t): t is string => typeof t === "string")
        : [],
      default_ttl_seconds:
        typeof help.default_ttl_seconds === "number"
          ? help.default_ttl_seconds
          : DEFAULT_CONFIG.help_request.default_ttl_seconds,
      max_active_per_player:
        typeof help.max_active_per_player === "number"
          ? help.max_active_per_player
          : DEFAULT_CONFIG.help_request.max_active_per_player,
    },
    rate_limits: {
      auth_per_ip_per_minute:
        typeof rate.auth_per_ip_per_minute === "number"
          ? rate.auth_per_ip_per_minute
          : DEFAULT_CONFIG.rate_limits.auth_per_ip_per_minute,
      help_create_per_minute:
        typeof rate.help_create_per_minute === "number"
          ? rate.help_create_per_minute
          : DEFAULT_CONFIG.rate_limits.help_create_per_minute,
    },
    profile: {
      stats_schema: Array.isArray(profile.stats_schema)
        ? (profile.stats_schema as ProjectConfig["profile"]["stats_schema"])
        : [],
      meta_schema: normalizeMetaSchema(profile.meta_schema),
      allowed_avatars: Array.isArray(profile.allowed_avatars)
        ? profile.allowed_avatars.filter(
            (s): s is string => typeof s === "string",
          )
        : [],
    },
    guild_assets: {
      allowed_logos: Array.isArray(guildAssets.allowed_logos)
        ? guildAssets.allowed_logos.filter(
            (s): s is string => typeof s === "string",
          )
        : [],
    },
  };
}
